import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { formatCurrency } from '../../mock';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { CsvButton, DataTable, GroupTabs, PeriodBar, Ribbon, Select, PRESETS } from './shared';

const GROUPS = [['salesperson', 'Vendedor'], ['lead_source', 'Fonte do lead'], ['make', 'Marca'], ['month', 'Mês'], ['finance_type', 'Pagamento']];
const money = (v) => formatCurrency(v || 0);

export const SalesReport = ({ opts, mine }) => {
  const { user } = useAuth();
  const canCancel = ['Administrator', 'Owner'].includes(user?.role);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [canceling, setCanceling] = useState(false);
  const month = PRESETS().find(([k]) => k === 'month')[2];
  const [range, setRange] = useState(month);
  const [f, setF] = useState({ salesperson: '', lead_source: '', finance_type: '', make: '', price_min: '', price_max: '' });
  const [data, setData] = useState(null);
  const [group, setGroup] = useState('salesperson');
  const [loading, setLoading] = useState(true);
  const fin = !!data?.fin_view;

  const qs = useCallback((r = range, ff = f) => {
    const p = new URLSearchParams({ date_from: r.from || '', date_to: r.to || '', ...ff });
    if (mine) p.set('mine', 'true');
    return p.toString();
  }, [range, f, mine]);

  const load = useCallback(async (r, ff) => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/sales?${qs(r || range, ff || f)}`); setData(data); }
    catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [qs, range, f]);

  useEffect(() => { load(month); }, []); // eslint-disable-line

  const k = data?.kpis || {};
  const rows = data?.rows || [];

  const cols = [
    { key: 'sale_date', label: 'DATA', render: (r) => <span className="num text-muted-foreground">{r.sale_date}</span> },
    { key: 'deal_id', label: 'DEAL', render: (r) => <span className="num text-[11px] text-muted-foreground">{r.deal_id}</span> },
    { key: 'vehicle', label: 'VEÍCULO', render: (r) => (<div><p className="font-semibold">{r.vehicle}</p><p className="text-[10px] text-muted-foreground/70">#{r.stock || '—'} · {r.days_in_stock}d em estoque</p></div>) },
    { key: 'customer', label: 'CLIENTE', hide: 'hidden xl:table-cell' },
    { key: 'salesperson', label: 'VENDEDOR' },
    { key: 'lead_source', label: 'FONTE', render: (r) => <span className="ent-tag">{r.lead_source}</span> },
    { key: 'finance_type', label: 'PAGTO', hide: 'hidden lg:table-cell' },
    { key: 'days_to_close', label: 'FECHOU EM', align: 'right', render: (r) => (r.days_to_close == null ? '—' : `${r.days_to_close}d`) },
    { key: 'sale_price', label: 'VENDA', align: 'right', render: (r) => money(r.sale_price) },
    ...(fin ? [
      { key: 'front_gross', label: 'FRONT', align: 'right', render: (r) => money(r.front_gross) },
      { key: 'backend_net', label: 'BACK-END', align: 'right', render: (r) => <span className="t-warn">{money(r.backend_net)}</span> },
      { key: 'profit', label: 'LUCRO', align: 'right', render: (r) => <span className={r.profit >= 0 ? 't-ok font-bold' : 't-bad font-bold'}>{money(r.profit)}</span> },
    ] : []),
    ...(canCancel ? [{
      key: 'cancel', label: 'AÇÃO', align: 'right',
      render: (r) => (
        <button data-testid={`cancel-sale-${r.deal_id}`} onClick={(e) => { e.stopPropagation(); setCancelRow(r); setCancelReason(''); }}
          className="ent-btn ent-btn-sm hover:text-[hsl(var(--bad))]">Cancelar venda</button>
      ),
    }] : []),
  ];

  const confirmCancel = async () => {
    if (!cancelRow) return;
    setCanceling(true);
    try {
      const { data: res } = await api.post(`/deals/${cancelRow.deal_id}/cancel`, { reason: cancelReason });
      toast({ title: 'Venda cancelada', description: `${cancelRow.vehicle} ${res.vehicle_returned ? '· veículo devolvido ao estoque' : ''}` });
      setCancelRow(null);
      await load();
    } catch (e) { toast({ title: 'Não foi possível cancelar', description: e.response?.data?.detail || '' }); }
    finally { setCanceling(false); }
  };

  const gcols = [
    { key: 'key', label: GROUPS.find(([g]) => g === group)[1].toUpperCase(), render: (r) => <span className="font-semibold">{r.key}</span> },
    { key: 'units', label: 'UNID.', align: 'right' },
    { key: 'revenue', label: 'RECEITA', align: 'right', render: (r) => money(r.revenue) },
    { key: 'avg_price', label: 'TICKET MÉDIO', align: 'right', render: (r) => money(r.avg_price) },
    ...(fin ? [
      { key: 'front_gross', label: 'FRONT', align: 'right', render: (r) => money(r.front_gross) },
      { key: 'backend_net', label: 'BACK-END', align: 'right', render: (r) => <span className="t-warn">{money(r.backend_net)}</span> },
      { key: 'profit', label: 'LUCRO', align: 'right', render: (r) => <span className={r.profit >= 0 ? 't-ok font-bold' : 't-bad font-bold'}>{money(r.profit)}</span> },
      { key: 'avg_profit', label: 'LUCRO/CARRO', align: 'right', render: (r) => money(r.avg_profit) },
      { key: 'backend_penetration', label: 'PENETRAÇÃO BE', align: 'right', render: (r) => `${r.backend_penetration}%` },
    ] : []),
    { key: 'avg_days_to_close', label: 'DIAS P/ FECHAR', align: 'right', render: (r) => `${r.avg_days_to_close}d` },
    { key: 'avg_days_in_stock', label: 'DIAS EM ESTOQUE', align: 'right', render: (r) => `${r.avg_days_in_stock}d` },
  ];

  return (
    <div className="space-y-3" data-testid="sales-report">
      <PeriodBar range={range} setRange={setRange} onApply={(r) => load(r)} testid="sales-filters">
        {!mine && opts?.is_manager && <Select label="Vendedor" testid="filter-salesperson" value={f.salesperson} onChange={(v) => setF({ ...f, salesperson: v })} options={opts?.salespeople || []} />}
        <Select label="Fonte do lead" testid="filter-source" value={f.lead_source} onChange={(v) => setF({ ...f, lead_source: v })} options={opts?.sources || []} />
        <Select label="Pagamento" testid="filter-finance" value={f.finance_type} onChange={(v) => setF({ ...f, finance_type: v })} options={opts?.finance_types || []} />
        <Select label="Marca" testid="filter-make" value={f.make} onChange={(v) => setF({ ...f, make: v })} options={opts?.makes || []} />
        <div><p className="ent-label mb-1">Preço min</p><input data-testid="filter-price-min" className="ent-input num" style={{ width: 92 }} value={f.price_min} onChange={(e) => setF({ ...f, price_min: e.target.value.replace(/\D/g, '') })} /></div>
        <div><p className="ent-label mb-1">Preço máx</p><input data-testid="filter-price-max" className="ent-input num" style={{ width: 92 }} value={f.price_max} onChange={(e) => setF({ ...f, price_max: e.target.value.replace(/\D/g, '') })} /></div>
        <CsvButton testid="sales-csv" url={`/reports/sales/csv?${qs()}`} filename="relatorio_vendas.csv" />
      </PeriodBar>

      {loading ? <p className="ent-empty">Calculando relatório…</p> : !data ? <p className="ent-empty">Não foi possível carregar o relatório.</p> : (
        <>
          <Ribbon testid="sales-kpis" items={[
            ['Unidades vendidas', k.units],
            ['Receita', money(k.revenue)],
            ['Ticket médio', money(k.avg_price)],
            fin && ['Front gross', money(k.front_gross)],
            fin && ['Back-end líquido', money(k.backend_net), 't-warn'],
            fin && ['Lucro efetivo', money(k.profit), k.profit >= 0 ? 't-ok' : 't-bad'],
            fin && ['Lucro médio/carro', money(k.avg_profit)],
            ['Dias p/ fechar (méd.)', `${k.avg_days_to_close}d`],
            ['Dias em estoque (méd.)', `${k.avg_days_in_stock}d`],
            ['Entregues', k.delivered],
            ['Deals manuais (fora do cálculo)', k.manual_deals, 't-warn'],
          ]} />

          <div className="ent-panel">
            <div className="ent-panel-head"><span className="ent-label">Ranking e médias</span></div>
            <GroupTabs testid="sales-group" tabs={GROUPS} value={group} onChange={setGroup} />
            <DataTable testid="sales-group-table" cols={gcols} rows={data.groups?.[group] || []} rowKey="key" />
          </div>

          {fin && (
            <div className="ent-panel">
              <div className="ent-panel-head"><span className="ent-label">Produtos de back-end</span></div>
              <DataTable testid="sales-products" rowKey="key" rows={data.products || []} cols={[
                { key: 'label', label: 'PRODUTO', render: (r) => <span className="font-semibold">{r.label}</span> },
                { key: 'units', label: 'VENDIDOS', align: 'right' },
                { key: 'penetration', label: 'PENETRAÇÃO', align: 'right', render: (r) => `${r.penetration}%` },
                { key: 'markup', label: 'MARKUP', align: 'right', render: (r) => <span className="t-warn">{money(r.markup)}</span> },
              ]} />
            </div>
          )}

          <div className="ent-panel">
            <div className="ent-panel-head">
              <span className="ent-label">Vendas detalhadas</span>
              <span className="ent-pill">{data.row_count}</span>
            </div>
            <DataTable testid="sales-rows" cols={cols} rows={rows} rowKey="deal_id" empty="Nenhuma venda no período com esses filtros" />
          </div>
        </>
      )}

      <Dialog open={!!cancelRow} onOpenChange={(o) => !o && setCancelRow(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md" data-testid="cancel-sale-dialog">
          <DialogHeader><DialogTitle className="font-display font-bold text-lg uppercase tracking-tight">Cancelar venda</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[13px] text-foreground/80">
              Deal <b className="num">{cancelRow?.deal_id}</b> · {cancelRow?.vehicle} · {cancelRow?.customer}
            </p>
            <p className="text-[12px] t-warn border-l-2 border-[hsl(var(--warn))] pl-2.5">
              O veículo volta para o estoque como <b>Disponível</b>, o deal recebe o selo <b>CANCELADA</b> com seu nome
              e a venda sai de todas as métricas e comissões.
            </p>
            <div>
              <p className="ent-label mb-1">Motivo (opcional)</p>
              <textarea data-testid="cancel-sale-reason" rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                className="w-full bg-muted/40 border border-border rounded-md px-3 py-2 text-[13px] outline-none focus:border-primary transition-colors"
                placeholder="Ex: financiamento negado, cliente desistiu..." />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-sale-abort" onClick={() => setCancelRow(null)} className="ent-btn">Voltar</button>
            <button data-testid="cancel-sale-confirm" onClick={confirmCancel} disabled={canceling} className="ent-btn ent-btn-primary">{canceling ? 'Cancelando...' : 'Confirmar cancelamento'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
